import type { GridPoint } from '../../core/types';
import type { WorldMap } from '../../world/WorldMap';
import type { OwnerId, PlayerId } from '../ids';
import { buildingDef, statsAt, type BuildingDefinition, type BuildingTypeId } from './definitions';
import { footprintTiles } from './footprint';
import { OccupancyGrid } from './OccupancyGrid';
import { validatePlacement, type PlacementResult } from './placement';

export type ConstructionState = 'under_construction' | 'complete';

/** Read-only, serialisable view of a placed building. Mutated only by the store. */
export interface Building {
  /** Unique within a match, never reused (even after demolition). */
  readonly id: number;
  readonly type: BuildingTypeId;
  readonly ownerId: OwnerId;
  /** Footprint origin: the tile with the smallest grid x and y. */
  readonly origin: GridPoint;
  readonly state: ConstructionState;
  /** Construction progress 0..1 (1 when complete). */
  readonly progress: number;
  readonly health: number;
  readonly maxHealth: number;
  /** Part of the match's starting state rather than placed by a player. */
  readonly preplaced: boolean;
  /** 1, or 2 once upgraded (Phase 8). */
  readonly level: number;
  /** Seconds of upgrade work done, or null when not upgrading. */
  readonly upgradeProgress: number | null;
}

interface MutableBuilding {
  id: number;
  type: BuildingTypeId;
  ownerId: OwnerId;
  origin: GridPoint;
  state: ConstructionState;
  progress: number;
  health: number;
  maxHealth: number;
  preplaced: boolean;
  level: number;
  upgradeProgress: number | null;
}

export type BuildingEvent =
  | { type: 'added'; building: Building }
  /** `destroyed` = health reached zero (Phase 5), as opposed to demolished by its owner. */
  | { type: 'removed'; building: Building; destroyed?: boolean }
  | { type: 'damaged'; building: Building; amount: number }
  | { type: 'progress'; building: Building }
  | { type: 'completed'; building: Building }
  /** Phase 8: upgrade work began / finished; repairs restored health. */
  | { type: 'upgradeStarted'; building: Building }
  | { type: 'upgraded'; building: Building }
  | { type: 'repaired'; building: Building; amount: number };

export interface StoreConfig {
  /** Ownership rule: players build within this many tiles of their own buildings. */
  readonly buildRadius: number;
}

/**
 * Owns every building in a match: unique ids, owners, the occupancy grid and
 * construction state. Engine-free; the client renderer and HUD only listen
 * to its events. Mutations happen only through the Simulation's commands.
 *
 * Phase 2 construction rule (provisional): a placed building completes on its
 * own after `buildTimeSeconds` of SIMULATION time; health rises with progress.
 * No resources are charged — there is no resource ledger yet (Phase 3).
 */
export class BuildingStore {
  readonly occupancy: OccupancyGrid;
  private readonly buildings = new Map<number, MutableBuilding>();
  private readonly listeners = new Set<(e: BuildingEvent) => void>();
  private nextId = 1;

  constructor(
    private readonly map: WorldMap,
    private readonly cfg: StoreConfig,
  ) {
    this.occupancy = new OccupancyGrid(map.width, map.height);
  }

  get count(): number {
    return this.buildings.size;
  }

  get listenerCount(): number {
    return this.listeners.size;
  }

  get buildRadius(): number {
    return this.cfg.buildRadius;
  }

  subscribe(l: (e: BuildingEvent) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  get(id: number): Building | null {
    const b = this.buildings.get(id);
    return b ? snapshot(b) : null;
  }

  /** Building occupying a tile, or null. */
  at(x: number, y: number): Building | null {
    const id = this.occupancy.at(x, y);
    return id ? this.get(id) : null;
  }

  /** All buildings, in id order (deterministic). */
  all(): Building[] {
    return [...this.buildings.values()].sort((a, b) => a.id - b.id).map(snapshot);
  }

  ownedBy(owner: OwnerId): Building[] {
    return this.all().filter((b) => b.ownerId === owner);
  }

  definition(id: number): BuildingDefinition | null {
    const b = this.buildings.get(id);
    return b ? buildingDef(b.type) : null;
  }

  /**
   * Validate a placement. With `owner`, the ownership (territory) rule applies;
   * without it (predefined starting structures) it does not.
   */
  validate(type: BuildingTypeId, origin: GridPoint, owner?: PlayerId): PlacementResult {
    return validatePlacement(
      {
        map: this.map,
        occupancy: this.occupancy,
        occupantName: (id) => this.definition(id)?.name ?? null,
        territory: owner
          ? {
              radius: this.cfg.buildRadius,
              isOwnTile: (x, y) => {
                const id = this.occupancy.at(x, y);
                return id !== 0 && this.buildings.get(id)?.ownerId === owner;
              },
            }
          : undefined,
      },
      type,
      origin,
    );
  }

  /**
   * Add a building. Callers (the Simulation) must validate first; this throws
   * if the footprint is not free, so an unvalidated placement can never
   * silently overlap.
   */
  add(type: BuildingTypeId, origin: GridPoint, ownerId: OwnerId, opts: { complete?: boolean; preplaced?: boolean } = {}): Building {
    const def = buildingDef(type);
    const done = opts.complete === true || def.buildTimeSeconds <= 0;
    const b: MutableBuilding = {
      id: this.nextId,
      type,
      ownerId,
      origin: { x: origin.x, y: origin.y },
      state: done ? 'complete' : 'under_construction',
      progress: done ? 1 : 0,
      maxHealth: def.maxHealth,
      health: done ? def.maxHealth : healthFor(def.maxHealth, 0),
      preplaced: opts.preplaced === true,
      level: 1,
      upgradeProgress: null,
    };
    this.occupancy.occupy(footprintTiles(b.origin, def.footprint), b.id);
    this.nextId++;
    this.buildings.set(b.id, b);
    this.emit({ type: 'added', building: snapshot(b) });
    return snapshot(b);
  }

  /** Remove a building and free its tiles. Callers check permissions first. */
  remove(id: number): Building | null {
    const b = this.buildings.get(id);
    if (!b) return null;
    this.buildings.delete(id);
    this.occupancy.release(id);
    this.emit({ type: 'removed', building: snapshot(b) });
    return snapshot(b);
  }

  /**
   * Deal damage (Phase 5). Health never goes below zero; at zero the building
   * is destroyed (removed, no refund). Returns true if this destroyed it.
   */
  damage(id: number, amount: number): boolean {
    const b = this.buildings.get(id);
    if (!b || !(amount > 0)) return false;
    const dealt = Math.min(b.health, Math.round(amount));
    b.health -= dealt;
    this.emit({ type: 'damaged', building: snapshot(b), amount: dealt });
    if (b.health > 0) return false;
    this.buildings.delete(id);
    this.occupancy.release(id);
    this.emit({ type: 'removed', building: snapshot(b), destroyed: true });
    return true;
  }

  /** Start the level-2 upgrade (the Simulation validates and charges first). */
  startUpgrade(id: number): void {
    const b = this.buildings.get(id);
    if (!b) return;
    b.upgradeProgress = 0;
    this.emit({ type: 'upgradeStarted', building: snapshot(b) });
  }

  /** Match setup only (raid bases): put a complete building straight at a level, at full health. */
  setupLevel(id: number, level: number): void {
    const b = this.buildings.get(id);
    if (!b || level < 1 || level > 2 || !buildingDef(b.type).upgrade && level > 1) return;
    b.level = level;
    b.maxHealth = statsAt(b.type, level).maxHealth;
    b.health = b.maxHealth;
  }

  /** Restore up to `amount` health (never above max). Returns what was restored. */
  repair(id: number, amount: number): number {
    const b = this.buildings.get(id);
    if (!b || !(amount > 0) || b.state !== 'complete') return 0;
    const healed = Math.min(b.maxHealth - b.health, Math.floor(amount));
    if (healed <= 0) return 0;
    b.health += healed;
    this.emit({ type: 'repaired', building: snapshot(b), amount: healed });
    return healed;
  }

  /** Advance construction and upgrades by dt seconds of simulation time. */
  tick(dt: number): void {
    if (!(dt > 0)) return;
    for (const id of [...this.buildings.keys()].sort((a, b) => a - b)) {
      const b = this.buildings.get(id)!;
      if (b.upgradeProgress !== null) {
        const up = buildingDef(b.type).upgrade!;
        b.upgradeProgress += dt;
        if (b.upgradeProgress >= up.seconds - 1e-9) {
          const max = statsAt(b.type, 2).maxHealth;
          b.health = Math.min(max, b.health + (max - b.maxHealth));
          b.maxHealth = max;
          b.level = 2;
          b.upgradeProgress = null;
          this.emit({ type: 'upgraded', building: snapshot(b) });
        }
        continue;
      }
      if (b.state !== 'under_construction') continue;
      const def = buildingDef(b.type);
      const before = healthFor(b.maxHealth, b.progress);
      b.progress = Math.min(1, b.progress + dt / def.buildTimeSeconds);
      // Values within float noise of 1 count as complete, so N ticks of buildTime/N always finish.
      if (b.progress >= 1 - 1e-9) b.progress = 1;
      // Construction ADDS health; damage taken while building is kept (Phase 6 fix, D65).
      b.health = Math.min(b.maxHealth, b.health + healthFor(b.maxHealth, b.progress) - before);
      if (b.progress === 1) {
        b.state = 'complete';
        this.emit({ type: 'completed', building: snapshot(b) });
      } else {
        this.emit({ type: 'progress', building: snapshot(b) });
      }
    }
  }

  /** Next id that will be assigned (for snapshots). */
  get nextBuildingId(): number {
    return this.nextId;
  }

  private emit(e: BuildingEvent): void {
    for (const l of this.listeners) l(e);
  }
}

/** Health of an UNDAMAGED building at this construction progress: 10% of max plus the rest in proportion (always ≥ 1; max at 1). */
export function healthFor(maxHealth: number, progress: number): number {
  return Math.max(1, Math.round(maxHealth * (0.1 + 0.9 * Math.min(1, Math.max(0, progress)))));
}

function snapshot(b: MutableBuilding): Building {
  return { ...b, origin: { ...b.origin } };
}
